import {
  LIST_FIELD_TYPE,
  RISK_CENTER_TYPES,
} from '@app/dashboard/constants/risk_center';
import {
  SYSTEM_EVENTS_GROUPING_CODES
} from '@app/dashboard/constants/tracking_events/system';
import {
  IDropdownOption,
  IListModalConfig,
  IRuleModalConfig,
} from '@app/dashboard/types/innerLayout/risk_center/modals';
import { IValidation } from '@app/dashboard/types/outerLayout/common';
import { TFunction } from 'i18next';

export const generateModalKeys = ({
  key,
  includeProp = true,
}: {
  key: string;
  includeProp?: boolean;
}) => {
  const firstLetterCapital = key.charAt(0).toUpperCase() + key.slice(1);

  return {
    TITLE_KEY: `innerLayout.riskCenter.${key}.addBtn`,
    TYPE_LABEL_KEY: `innerLayout.riskCenter.${key}.labels.${key}TypeLabel`,
    TYPE_LABEL_PLACEHOLDER_KEY: `innerLayout.riskCenter.${key}.placeholder.select${firstLetterCapital}TypePlaceholder`,
    ENABLED_LABEL_KEY: `innerLayout.riskCenter.${key}.labels.enabledLabel`,
    NOTE_KEY: `innerLayout.riskCenter.${key}.note`,
    NAME_LABEL_KEY: includeProp
      ? `innerLayout.riskCenter.${key}.labels.${key}NameLabel`
      : null,
    NAME_LABEL_PLACEHOLDER_KEY: includeProp
      ? `innerLayout.riskCenter.${key}.placeholder.input${firstLetterCapital}DataPlaceholder`
      : null,
    DATA_LABEL_KEY: includeProp
      ? `innerLayout.riskCenter.${key}.labels.${key}DataLabel`
      : null,
    DATA_LABEL_PLACEHOLDER_KEY: includeProp
      ? `innerLayout.riskCenter.${key}.placeholder.input${firstLetterCapital}DataPlaceholder`
      : null,
    CREATED_SUCCESSFULLY_KEY: `innerLayout.riskCenter.${key}.messages.${key}CreatedSuccessfully`,
    UPDATED_SUCCESSFULLY_KEY: `innerLayout.riskCenter.${key}.messages.${key}UpdatedSuccessfully`,
    DELETED_SUCCESSFULLY_KEY: `innerLayout.riskCenter.${key}.messages.${key}DeletedSuccessfully`,
    DEACTIVATED_SUCCESSFULLY_KEY: `innerLayout.riskCenter.${key}.messages.${key}DeactivatedSuccessfully`,
    ACTIVATED_SUCCESSFULLY_KEY: `innerLayout.riskCenter.${key}.messages.${key}ActivatedSuccessfully`,
    EDIT_MODAL_TITLE_KEY: `innerLayout.riskCenter.${key}.editModalTitle`,
    TRACKING_CODE_OBJ: (SYSTEM_EVENTS_GROUPING_CODES.RISK_CENTER.DETAILS as any)[key.toUpperCase()],
  };
};

export const normalizedOptions = (
  options: (IListModalConfig | IRuleModalConfig)[],
  lang: 'en' | 'ar'
): IDropdownOption[] =>
  options?.map((option) => {
    if ('name' in option && 'value' in option) {
      // IListModalConfig
      return { label: option.name, value: option.value, raw: option };
    } else if ('description' in option && 'id' in option) {
      // IRuleModalConfig
      return {
        label: lang === 'en' ? option.description : option.description_ar,
        value: option.id,
        raw: option,
      };
    }
    return null;
  }) as IDropdownOption[];

export const getValidations = (
  t: TFunction
): Record<string, IValidation[]> => ({
  EL: [
    {
      pattern: /^(?!\s*$).+/,
      error_msg: t('errors.required'),
    },
    {
      pattern: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/,
      error_msg: t('innerLayout.riskCenter.errors.invalidEmail'),
    },
  ],
  PH: [
    {
      pattern: /^(?!\s*$).+/,
      error_msg: t('errors.required'),
    },
    {
      pattern: /^[0-9]{9,}$/,
      error_msg: t('innerLayout.riskCenter.errors.invalidPhone'),
    },
  ],
  BN: [
    {
      pattern: /^([0-9]{6}|[0-9]{8})$/,
      error_msg: t('innerLayout.riskCenter.errors.invalidBIN'),
    },
  ],
  IP: [
    {
      pattern:
        /^(25[0-5]|2[0-4]\d|[01]?\d\d?)\.(25[0-5]|2[0-4]\d|[01]?\d\d?)\.(25[0-5]|2[0-4]\d|[01]?\d\d?)\.(25[0-5]|2[0-4]\d|[01]?\d\d?)$/,
      error_msg: t('innerLayout.riskCenter.errors.invalidIP'),
    },
  ],
  FP: [
    {
      pattern: /^[a-z0-9]{16}$/i,
      error_msg: t('innerLayout.riskCenter.errors.invalidFingerprint'),
    },
  ],
  PN: [
    {
      pattern: /^(\d{13}|\d{15}|\d{16})$/,
      error_msg: t('innerLayout.riskCenter.errors.invalidCardNumber'),
    },
  ],
  CO: [
    {
      pattern: /^[A-Z]{2}$/,
      error_msg: t('innerLayout.riskCenter.errors.invalidCountry'),
    },
  ],
});

export const handleTypeNameMapper = (riskType: string) => {
  switch (riskType) {
    case RISK_CENTER_TYPES.BLACKLIST:
      return { type: 'blacklist_type', data: 'blacklist_data' };
    case RISK_CENTER_TYPES.RULES:
      return { type: 'rule_type', data: 'rule_data' };
    case RISK_CENTER_TYPES.WHITELIST:
      return { type: 'whitelist_type', data: 'whitelist_data' };
    default:
      return '';
  }
};

export const getListTypeValue = (name: string = ''): string => {
  return (
    LIST_FIELD_TYPE[name.toUpperCase() as keyof typeof LIST_FIELD_TYPE] ?? ''
  );
};
